import { z } from 'zod';
import {
  dunningChannels,
  dunningNoticeStatuses,
  type DunningChannel,
  type DunningNoticeStatus,
} from '../enums.js';
import { paginationFields } from './_pagination.js';

/**
 * Contrat réseau des relances de paiement.
 *
 * Une relance naît toujours en `pending_validation` : la V1 envoyait depuis une tâche automatique
 * sans qu'aucun praticien ne relise la lettre. Le passage à `sent` est une action explicite, et
 * c'est elle qui choisit le canal.
 */

export const dunningIdParamSchema = z.object({ id: z.uuid() }).strict();

/**
 * Relance créée **à la main sur une facture précise**, avec le niveau choisi. Le balayage
 * automatique ne sait faire que « toutes les factures éligibles au niveau suivant » : un praticien
 * qui vient d'avoir le patient au téléphone veut souvent relancer celui-ci, maintenant, au niveau
 * qu'il juge bon.
 *
 * Les garde-fous de structure restent : facture émise ou partiellement payée, reste dû non nul, et
 * pas deux relances vivantes au même niveau. Seul le **délai** est passé outre, puisque c'est
 * précisément ce que le praticien décide d'anticiper.
 */
export const dunningManualCreateSchema = z
  .object({
    invoiceId: z.uuid(),
    level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  })
  .strict();
export type DunningManualCreateInput = z.infer<typeof dunningManualCreateSchema>;

/**
 * Action groupée. Une boucle côté client serait N appels sans progression fiable : un onglet fermé
 * au milieu laisse la moitié des relances dans un état que personne ne sait nommer. Ici un seul
 * appel, et un compte rendu **par relance**.
 */
const bulkIdsSchema = z.array(z.uuid()).min(1).max(100);

export const dunningBulkValidateSchema = z
  .object({ ids: bulkIdsSchema, channel: z.enum(dunningChannels).default('email') })
  .strict();
export type DunningBulkValidateInput = z.infer<typeof dunningBulkValidateSchema>;

export const dunningBulkCancelSchema = z.object({ ids: bulkIdsSchema }).strict();
export type DunningBulkCancelInput = z.infer<typeof dunningBulkCancelSchema>;

/**
 * Sort d'une relance dans une action groupée. `downloaded` n'est pas un échec : la relance est
 * bien validée, mais le patient n'a pas d'adresse et la lettre reste à imprimer et à poster.
 */
export const dunningBulkOutcomes = ['sent', 'downloaded', 'cancelled', 'failed'] as const;
export type DunningBulkOutcome = (typeof dunningBulkOutcomes)[number];

export interface DunningBulkItemDto {
  id: string;
  outcome: DunningBulkOutcome;
  /** Canal effectif, nul pour une annulation ou un échec. */
  channel: DunningChannel | null;
  /** Code d'erreur stable quand `failed`, pour que l'écran nomme la cause sans la deviner. */
  errorCode: string | null;
}

export interface DunningBulkResultDto {
  items: DunningBulkItemDto[];
  succeeded: number;
  failed: number;
}

/**
 * Validation d'une relance. `email` est une intention : sans adresse chez le patient ou sans
 * fournisseur d'email, le serveur bascule sur `download` et le dit dans la réponse plutôt que
 * d'échouer — la lettre reste à imprimer.
 */
export const dunningValidateSchema = z
  .object({ channel: z.enum(dunningChannels).default('email') })
  .strict();
export type DunningValidateInput = z.infer<typeof dunningValidateSchema>;

export const dunningListQuerySchema = z
  .object({
    status: z.enum(dunningNoticeStatuses).optional(),
    invoiceId: z.uuid().optional(),
    /** La consultation de la **facture** relancée : une relance n'en porte pas elle-même. */
    encounterId: z.uuid().optional(),
    ...paginationFields,
  })
  .strict();
export type DunningListQuery = z.infer<typeof dunningListQuerySchema>;

/**
 * Une relance, **telle que le centre de pilotage l'affiche**. Le contrat portait la seule ligne de
 * relance : ni patient, ni numéro de facture, donc une liste incapable de dire de qui et de quoi
 * elle parle. Les champs rapportés ci-dessous sont exactement ceux que la V1 affichait dans cet
 * écran, plus les jours de retard — qu'elle calculait, envoyait, et n'affichait jamais.
 *
 * Le **nom vient du dossier vivant**, pas d'un instantané pris à la facturation : un dossier
 * anonymisé au titre du RGPD doit disparaître aussi de cet écran, ce qu'un instantané empêcherait.
 *
 * L'adresse du patient n'y est **pas** : la liste n'a besoin que de savoir si l'envoi par email est
 * possible, et la route d'envoi résout le destinataire elle-même. La V1 expédiait la ligne patient
 * entière au navigateur pour en afficher une sous-ligne.
 */
export interface DunningNoticeDto {
  id: string;
  invoiceId: string;
  level: number;
  status: DunningNoticeStatus;
  /** Intérêts de retard du niveau, calculés sur le total TTC de la facture. */
  interestCents: number;
  /** Total réclamé par la lettre : reste dû du moment plus les intérêts du niveau. */
  dueCents: number;
  generatedAt: string;
  validatedBy: string | null;
  sentAt: string | null;
  channel: DunningChannel | null;
  documentId: string | null;
  createdAt: string;

  /* De qui et de quoi il s'agit. */
  patientId: string;
  patientName: string;
  /** Vrai quand le dossier porte une adresse : c'est ce qui rend l'envoi par email possible. */
  patientHasEmail: boolean;
  invoiceNumber: string | null;
  invoiceIssueDate: string;
  invoiceDueDate: string | null;
  invoiceTotalCents: number;
  /**
   * Jours de retard à la date de génération de la relance, ou `null` quand la facture n'a pas
   * d'échéance. Calculé à la lecture : le stocker le figerait à une date qui n'est plus.
   */
  daysOverdue: number | null;
}

/**
 * Totaux du centre de pilotage, calculés **sur le filtre et non sur la page**. La V1 les sommait
 * dans le navigateur, ce qu'une liste paginée rend faux : on afficherait le total de vingt lignes
 * en croyant afficher celui de deux cents.
 */
export interface DunningTotalsDto {
  /** Somme des montants réclamés. */
  dueCents: number;
  /** Dont intérêts — la mention que l'écran porte sous le montant total. */
  interestCents: number;
}

/* -------------------------------------------------------------------------- */
/* Historique d'envoi                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Un envoi d'une relance. `sentAt` et `channel` sur la relance elle-même disent le **dernier** ;
 * ici on a les précédents, parce qu'une relance renvoyée écrasait leur trace — et sur une
 * procédure de recouvrement, savoir combien de fois on a relancé et par quel canal est
 * précisément ce qu'on doit pouvoir montrer.
 *
 * L'adresse du destinataire n'y est pas : elle vit sur le dossier, et la recopier ici la ferait
 * survivre à une anonymisation RGPD.
 */
export interface DunningSendingDto {
  id: string;
  channel: DunningChannel;
  sentAt: string;
  /** Lettre produite pour cet envoi. Nulle si le document a été purgé depuis. */
  documentId: string | null;
  /** Qui a validé l'envoi. Nul si le compte a depuis été effacé. */
  sentByName: string | null;
}

export {
  DUNNING_EMAIL_BODY_MAX_LENGTH,
  DUNNING_EMAIL_SUBJECT_MAX_LENGTH,
  dunningEmailDraftTextsSchema,
  dunningEmailPreviewSchema,
  dunningEmailTextsSchema,
  dunningEmailVariables,
  dunningLevelTemplateSchema,
  dunningTemplatePlaceholders,
  dunningTemplatesReplaceSchema,
  normalizeDunningEmailText,
  unknownDunningEmailVariables,
} from './dunning-email.js';
export type {
  DunningEmailDefaultTexts,
  DunningEmailPreviewDto,
  DunningEmailPreviewInput,
  DunningEmailTemplateDto,
  DunningEmailTexts,
  DunningStoredTexts,
  DunningTemplatesReplaceInput,
} from './dunning-email.js';
